import React, { useRef } from "react";
import { toast } from "react-toastify";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";
import AddIcon from "@mui/icons-material/Add";
import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material";

import Modal from "Components/Common/Modal";
import SelectField from "Components/Common/SelectField";
import Tooltip from "Components/Common/Tooltip";
import InputFieldText from "Components/Common/InputFieldText";
import { gstOptions } from "common/enums";
import { DocumentName, UploadImage } from "pages/Admin/TechnicalSanction/style";

import getFileExtension from "utils/getFileExtension";

import { usePutUpdateApplicantFieldsMutation } from "apis/ApplicationRequestDetail";

import pdfImg from "assets/images/pdf.png";

const validFileFormats = ["jpg", "png", "jpeg", "pdf"];

const GSTNumberEditField = ({
  t,
  applicationData: {
    applicationId,
    isGSTRegistered: isGstNumberAvailable,
    gstNumber,
    panNumber,
  },
  handleClose,
}) => {
  const ref = useRef();

  const [updateGstNumber, { isLoading }] =
    usePutUpdateApplicantFieldsMutation();

  const formik = useFormik({
    initialValues: {
      gstNumber: gstNumber ?? "",
      isGSTRegistered: isGstNumberAvailable ?? "",
      file: "",
    },
    validationSchema: Yup.object({
      isGSTRegistered: Yup.string().required("GST is required"),

      gstNumber: Yup.string()
        .test("isGstRequired", "", function (_, context) {
          const [{ value: { isGSTRegistered, gstNumber } } = {}] =
            context.from || [];
          if (isGSTRegistered && !gstNumber) {
            return context.createError({
              message: "GST number is required",
            });
          }
          return true;
        })
        .matches(
          /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}[Zz][0-9A-Za-z]{1}$/,
          "Please enter a valid GST number"
        ),
    }),

    onSubmit: async ({ gstNumber, file }) => {
      try {
        if (file || !isGSTRegistered) {
          const formData = new FormData();
          formData.append("file", file.file);
          await updateGstNumber({
            query: { panNumber, applicationId, gstNumber },
            payload: formData,
            toastMessage: "GST number updated successfully",
          });
        } else {
          toast.error("Please upload gst document");
        }
      } catch (error) {}
    },
  });

  const {
    setFieldValue,
    handleSubmit,
    values: { isGSTRegistered, file },
  } = formik;

  const fileChangeHandler = (e) => {
    const file = e.target.files[0];

    const isAllFileValid =
      validFileFormats.includes(getFileExtension(file.name.toLowerCase())) &&
      file.size < 2097152;

    if (!isAllFileValid) {
      toast.error(
        "Please upload image or pdf file and size should be less than 2 mb"
      );
    } else {
      setFieldValue("file", {
        url: URL.createObjectURL(file),
        name: file.name,
        file: file,
      });
      ref.current.value = "";
    }
  };

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      title={"Edit GST Number"}
      maxWidth="sm"
    >
      <Grid container columnSpacing={2} className="mt-3">
        <Grid xs={12}>
          <SelectField
            label={t("GST") + "*"}
            name="isGSTRegistered"
            onChange={(_, { value }) => {
              setFieldValue("isGSTRegistered", value);
              if (!value) {
                setFieldValue("gstNumber", "");
              }
            }}
            formik={formik}
            options={gstOptions}
          />
        </Grid>
        {isGSTRegistered && (
          <>
            <Grid xs={12}>
              <InputFieldText
                label={t("GST Number*")}
                name="gstNumber"
                formik={formik}
                onChange={(e) =>
                  setFieldValue("gstNumber", e.target.value.toUpperCase())
                }
                placeholder="GST Number"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6">{t("Document")} :</Typography>
            </Grid>
            <Grid xs={12} className="ms-3">
              {file ? (
                <div className="w-50 mt-3">
                  <div className="image-container">
                    <a href={file.url} target="_blank" rel="noreferrer">
                      <Box
                        component="img"
                        className="image d-block m-auto"
                        alt="document image"
                        src={
                          getFileExtension(file.name.toLowerCase()) === "pdf"
                            ? pdfImg
                            : file.url
                        }
                      />
                    </a>
                    <i
                      className="ri-close-circle-line fs-3 close-btn"
                      onClick={() => {
                        setFieldValue("file", "");
                      }}
                    />
                    <Tooltip title={file.name}>
                      <DocumentName fontSize={13} variant="body2">
                        {file.name}
                      </DocumentName>
                    </Tooltip>
                  </div>
                </div>
              ) : (
                <Box component="label" className="m-0 p-0 cursor-pointer">
                  <UploadImage isTechnicalFeasibility>
                    <AddIcon
                      sx={{
                        color: "black",
                      }}
                    />
                  </UploadImage>
                  <input
                    type="file"
                    ref={ref}
                    accept="image pdf/*"
                    onChange={fileChangeHandler}
                    multiple
                    hidden
                  />
                </Box>
              )}
            </Grid>
          </>
        )}
        <Grid className="d-block ms-auto" mt={1}>
          <Button
            color="primary"
            size="small"
            variant="outlined"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {t("Save")}
            {isLoading && <CircularProgress size={20} />}
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};

GSTNumberEditField.propTypes = {
  t: PropTypes.func,
  applicationData: PropTypes.object,
  handleClose: PropTypes.func,
};

export default withTranslation()(GSTNumberEditField);
